import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useToast } from '../../hooks/useToast';
import { api } from '../../lib/api';
import { GlassCard } from '../../components/GlassCard';
import { RotateCcw, ArrowLeft, Package } from 'lucide-react';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function CreditNoteDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);
    api.creditNoteGet(token, id)
      .then(r => setData(r))
      .catch(e => { if (e.status !== 401) toast.show(e.message || 'Failed to load', 'error'); })
      .finally(() => setLoading(false));
  }, [token, id, toast]);

  const cn = data?.credit_note || data;
  const journal = data?.journal;
  const sym = cn?.currency_symbol_snapshot || '₹';

  const fmtMoney = (v) => {
    if (v == null) return '—';
    return `${sym}${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="credit-note-detail-page">
        <div className="py-20 flex justify-center"><div className="w-6 h-6 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" /></div>
      </div>
    );
  }

  if (!cn) {
    return (
      <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="credit-note-detail-page">
        <GlassCard className="p-12 text-center">
          <p className="text-gray-500">Credit note not found.</p>
          <Link to="/notes/credit" className="text-brand-purple text-sm font-bold hover:underline mt-2 inline-block">Back to credit notes</Link>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="credit-note-detail-page">
      <Link to="/notes/credit" className="inline-flex items-center gap-1.5 text-gray-500 text-sm font-semibold hover:text-gray-700 mb-4" data-testid="cn-back">
        <ArrowLeft size={14} /> Back to Credit Notes
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-orange-50 border border-orange-200 flex items-center justify-center">
            <RotateCcw size={24} className="text-orange-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-gray-900 font-extrabold text-xl">{cn.note_no}</h1>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-orange-50 text-orange-600 border border-orange-200">Credit Note</span>
            </div>
            <p className="text-gray-500 text-sm mt-0.5">{fmtDate(cn.date)} · {cn.customer_name}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-red-600 font-extrabold text-2xl tabular-nums" data-testid="cn-total">{fmtMoney(cn.total_returned)}</p>
          <p className="text-gray-400 text-xs mt-0.5">{cn.tax_label_snapshot || 'Tax'}: {fmtMoney(cn.tax_returned)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Sale reference */}
          {cn.sale_id && (
            <GlassCard className="p-4">
              <p className="text-gray-400 text-xs font-bold uppercase mb-1">Linked Sale</p>
              <p className="text-gray-700 text-sm font-semibold">{cn.sale_id.slice(0, 12)}...</p>
            </GlassCard>
          )}

          {/* Returned lines */}
          <GlassCard className="overflow-hidden" data-testid="cn-lines-card">
            <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
              <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Returned Items</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Product</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Qty</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Price</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">{cn.tax_label_snapshot || 'Tax'}</th>
                  <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Total</th>
                </tr>
              </thead>
              <tbody>
                {(cn.line_items || []).map((l, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-5 py-2.5 text-gray-900 font-semibold">{l.product_name}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-gray-700">{l.qty_returned}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-gray-700">{fmtMoney(l.unit_price)}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-gray-500">{fmtMoney(l.tax_amount)}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums text-gray-900 font-bold">{fmtMoney(l.line_total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-50">
                  <td colSpan={4} className="px-5 py-2.5 text-gray-900 font-extrabold text-xs uppercase text-right">Total Returned</td>
                  <td className="px-5 py-2.5 text-right text-red-600 font-extrabold tabular-nums">{fmtMoney(cn.total_returned)}</td>
                </tr>
              </tfoot>
            </table>
          </GlassCard>

          {/* Journal entry */}
          {journal && (
            <GlassCard className="overflow-hidden" data-testid="cn-journal-card">
              <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
                <p className="text-gray-500 text-xs font-bold uppercase tracking-wider">Journal Entry</p>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Account</th>
                    <th className="text-left px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Type</th>
                    <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Debit</th>
                    <th className="text-right px-5 py-2.5 text-gray-500 text-xs font-bold uppercase">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {(journal.lines || []).map((l, i) => (
                    <tr key={i} className="border-b border-gray-100">
                      <td className="px-5 py-2.5 text-gray-900 font-semibold">{l.account_name}</td>
                      <td className="px-5 py-2.5"><span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] font-bold uppercase">{l.account_type}</span></td>
                      <td className="px-5 py-2.5 text-right tabular-nums text-gray-700">{l.debit_rupees > 0 ? fmtMoney(l.debit_rupees) : '—'}</td>
                      <td className="px-5 py-2.5 text-right tabular-nums text-gray-700">{l.credit_rupees > 0 ? fmtMoney(l.credit_rupees) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 bg-gray-50">
                    <td colSpan={2} className="px-5 py-2.5 text-gray-900 font-extrabold text-xs uppercase">Totals</td>
                    <td className="px-5 py-2.5 text-right text-gray-900 font-extrabold tabular-nums">{fmtMoney(journal.debit_total)}</td>
                    <td className="px-5 py-2.5 text-right text-gray-900 font-extrabold tabular-nums">{fmtMoney(journal.credit_total)}</td>
                  </tr>
                </tfoot>
              </table>
            </GlassCard>
          )}
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          {/* Details */}
          <GlassCard className="p-5">
            <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-3">Details</h3>
            <div className="space-y-2 text-sm">
              {cn.reason && <div><p className="text-gray-400 text-xs font-bold uppercase">Reason</p><p className="text-gray-900 font-semibold mt-0.5">{cn.reason}</p></div>}
              {cn.notes && <div><p className="text-gray-400 text-xs font-bold uppercase">Notes</p><p className="text-gray-700 mt-0.5">{cn.notes}</p></div>}
              <div className="flex justify-between"><span className="text-gray-400">Created</span><span className="text-gray-700">{fmtDate(cn.created_at)}</span></div>
            </div>
          </GlassCard>

          {/* Stock movements */}
          {cn.stock_movements && cn.stock_movements.length > 0 && (
            <GlassCard className="p-5" data-testid="cn-stock-movements">
              <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider mb-3">Stock Movements</h3>
              <div className="space-y-2">
                {cn.stock_movements.map((m, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Package size={14} className="text-gray-400" />
                      <span className="text-gray-700">{m.product_name}</span>
                    </div>
                    <span className={`font-bold tabular-nums ${m.delta > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {m.delta > 0 ? '+' : ''}{m.delta}
                    </span>
                  </div>
                ))}
              </div>
            </GlassCard>
          )}
        </div>
      </div>
    </div>
  );
}
